import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import moment from 'moment';
import _, { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import Text from '../../components/Text';
import StyleButton from '../../components/StyleButton';
import StyleInput from '../../components/StyleInput';
import DateFilter from '../BedStatus/components/InternalBooking/DateFilter';
import { SearchIcon } from '../../components/Icon';
import { patientSurveyAPI } from '../../apis';
import VirtualizedList from '../../components/Virtualized/VirtualizedList';
import AutoCompleteMultiCheckbox from '../../components/AutoCompleteMultiCheckbox';
import { i18n } from '../../language';
import { authNameE } from '../../utils/userFuncs';
import useGetIpasMapping from '../../utils/hooks/useGetIpasMapping';
import { useGetUserInfo } from '../../utils';
import { SetAppreciationTotal } from '../../redux/actions';
import PatientInfoCard from './PatientInfoCard';
import SurveyGenQRCodePopup from './Popups/SurveyGenQRCodePopup';
import ExportSurveyPopup from './Popups/ExportSurveyPopup';
import SenderContactPopup from './Popups/SenderContactPopup';
import ShareSurveyPopup from './Popups/ShareSurveyPopup';
import EditAppreciationPopup from './Popups/EditAppreciationPopup'

const pageSize = 20;

const PatientSurvey = () => {
  const classes = useStyles();
  const [internalRefNo, setInternalRefNo] = useState('');
  const [qrCodePopup, setQrCodePopup] = useState(false);
  const [exportPopup, setExportPopup] = useState(false);
  const [senderContactPopup, setSenderContactPopup] = useState({ open: false, responseDetailId: null });
  const [shareSurveyPopup, setShareSurveyPopup] = useState({ open: false, responseId: null });
  const [editAppreciationPopup, setEditAppreciationPopup] = useState({ open: false, data: null });
  const [filter, setFilter] = useState({
    startDate: moment().add(-2, 'years'),
    endDate: moment(),
    ipasWardLocations: [],
  });
  const [list, setList] = useState({
    data: [],
    count: 0,
  });
  const rouList = useSelector((state) => state.rouList);
  const [pageNum, setPageNum] = useState(1);
  const [filterList, setFilterList] = useState([]);
  const language = i18n.locale === 'en-us' ? 'En' : 'Zh';
  const roleMap = useSelector((state) => state.roleMap);
  const [ipasMapping, getSysDataMappingList] = useGetIpasMapping();
  const userInfo = useGetUserInfo();
  const dispatch = useDispatch();
  const loginInfo = useSelector((state) => state.loginInfo);

  let scrollLock = false;

  console.log('filter', filter);

  useEffect(() => {
    if (!roleMap?.[authNameE.view_and_share_all_wards_appreciation]) {
      getSysDataMappingList();
    }
  }, [roleMap?.[authNameE.view_and_share_all_wards_appreciation], rouList]);

  // 防抖函數
  const debouncedSearch = useRef(
    debounce((type, value) => {
      setFilter((prev) => ({ ...prev, [type]: value }));
      setPageNum(1);
    }, 1000)
  ).current;

  // 清除副作用
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const locationList = useMemo(() => {
    return _.map(filterList, (item) => ({
      text: `${item?.ipasHospitalCode} ${item?.ipasWardLocation}`,
      value: item,
    }));
  }, [filterList]);

  const filterWardLocation = {
    // 除了PRO admin，其他用户的ipasHospitalCodes和ipasWardLocations要用当前登录的信息塞进来
    ipasHospitalCodes: roleMap?.[authNameE.view_and_share_all_wards_appreciation]
      ? _.uniqWith(
        _.map(filter.ipasWardLocations?.length === 0 ? locationList : filter.ipasWardLocations, (item) =>
          String(item.value.ipasHospitalCode)
        )
      )
      : _.uniqWith(_.map(ipasMapping.ipasMapping, (item) => String(item.hospCode))),
    ipasWardLocations: roleMap?.[authNameE.view_and_share_all_wards_appreciation]
      ? _.uniqWith(
        _.map(filter.ipasWardLocations?.length === 0 ? [] : filter.ipasWardLocations, (item) =>
          String(item.value.ipasWardLocation)
        )
      )
      : _.uniqWith(_.map(ipasMapping.ipasMapping, (item) => String(item.wardCode))),
  };

  const getPatientSurveyList = async (currentPageNum) => {
    try {
      const pageNo = currentPageNum ?? pageNum;
      scrollLock = true;
      // 开始时间跟结束时间都有的时候或者都没有的时候才call API
      if (
        ((moment(filter.startDate).isValid() && moment(filter.endDate).isValid()) ||
          (!moment(filter.startDate).isValid() && !moment(filter.endDate).isValid())) &&
        moment(filter.startDate).isSameOrBefore(filter.endDate)
      ) {
        const params = {
          endDate: moment(filter.endDate).hour(23).minute(59).second(59).format('YYYY-MM-DD HH:mm:ss'),
          refNo: internalRefNo,
          startDate: moment(filter.startDate).hour(0).minute(0).second(0).format('YYYY-MM-DD HH:mm:ss'),
          pageNum: pageNo,
          pageSize,
          ...filterWardLocation,
        };

        // PRO 用户 call all list
        const apiPath = roleMap?.[authNameE.view_and_share_all_wards_appreciation]
          ? 'getPatientSurveyAllList'
          : 'getPatientSurveyList';
        const { result } = await patientSurveyAPI[apiPath](params);
        pageNo > 1
          ? setList((prevList) => ({
            count: result.count,
            data: [...prevList.data, ...result.data],
          }))
          : setList(result);

        scrollLock = false;
      }
    } catch (error) {
      scrollLock = false;
      console.log(error);
    }
  };

  useEffect(() => {
    if (
      (roleMap?.[authNameE.view_and_share_all_wards_appreciation] && locationList.length > 0) ||
      _.some(ipasMapping.ipasMapping, (item) => item.wardCode && item.hospCode)
    ) {
      setPageNum(1);
      getPatientSurveyList(1);
    }
  }, [
    filter,
    rouList,
    roleMap?.[authNameE.view_and_share_all_wards_appreciation],
    ipasMapping.ipasMapping,
    locationList,
  ]);

  const getPatientFilterList = async () => {
    try {
      const params = {
        dwpHospitalCode: userInfo.hospCode,
      };
      const { result } = await patientSurveyAPI.getPatientAllFilterList(params);
      setFilterList(result);
    } catch (error) { }
  };

  useEffect(() => {
    if (roleMap?.[authNameE.view_and_share_all_wards_appreciation]) {
      getPatientFilterList();
    }
  }, [rouList]);

  const handleScroll = ({ scrollTop, clientHeight, scrollHeight }) => {
    if (list?.data?.length >= list?.count || scrollLock) {
      return;
    }
    if (scrollTop + clientHeight + 1 >= scrollHeight) {
      setPageNum((prevNum) => {
        getPatientSurveyList(prevNum + 1);
        return prevNum + 1;
      });
    }
  };

  const rowRender = (props) => {
    return (
      <PatientInfoCard
        setSenderContactPopup={setSenderContactPopup}
        setShareSurveyPopup={setShareSurveyPopup}
        setEditAppreciationPopup={setEditAppreciationPopup}
        {...props}
      />
    );
  };

  const logPatientAppreciationAccess = async () => {
    try {
      const { statusCode } = await patientSurveyAPI.logPatientAppreciationAccess(
        {
          sysRoleId: userInfo.roleTypeId,
          corpId: loginInfo.corpID,
          type: "Appreciation"
        }
      );
      if (statusCode === "0") {
        dispatch(SetAppreciationTotal(0))
      }
    } catch (error) { console.log('Log patient appreciation access error: ', error); }
  };

  useEffect(() => {
    logPatientAppreciationAccess()
  }, [])

  return (
    <Box className={classes.root}>
      {/* patient survey list */}
      <Box display="flex" flexDirection="column" gridGap="1.25rem" height="100%">
        <Text fontSize="2.25rem" color="#2E4469" fontWeight="700">
          Patient Appreciation
        </Text>
        <Box
          mt="0.62rem"
          borderRadius="1rem"
          boxShadow="0px 7px 16px 0px rgba(0, 0, 0, 0.04)"
          bgcolor="#FFFFFF"
          p="1.88rem"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pb="1.38rem"
            borderBottom="solid 1px #E4E7EB"
          >
            <Text fontSize="1.5rem" color="#333" fontWeight="700">
              Appreciation ({list.count ?? 0})
            </Text>
            <Box display="flex" alignItems="center" gridGap="1.25rem">
              {roleMap?.[authNameE.view_and_share_all_wards_appreciation] && (
                <AutoCompleteMultiCheckbox
                  width="13rem"
                  height="3rem"
                  itemList={locationList}
                  value={filter.ipasWardLocations}
                  onChange={(value) => {
                    setPageNum(1);
                    setFilter((prev) => ({ ...prev, ipasWardLocations: value }));
                  }}
                />
              )}
              {roleMap?.[authNameE.gen_appreciation_qr_code] && (
                <StyleButton
                  type="fill"
                  text="Generate QR Code"
                  width="auto"
                  p="0.88rem 1.88rem"
                  onClick={() => setQrCodePopup(true)}
                />
              )}
              {roleMap?.[authNameE.export_all_appreciation] && (
                <StyleButton
                  type="fill"
                  text="Export"
                  width="auto"
                  p="0.88rem 1.88rem"
                  onClick={() => setExportPopup(true)}
                />
              )}
            </Box>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" pt="1.88rem">
            <StyleInput
              value={internalRefNo}
              onChange={(value) => {
                setInternalRefNo(value);
              }}
              onKeyDown={(event) => {
                if (event.code === 'Enter' || event.code === 'NumpadEnter') {
                  setPageNum(1);
                  getPatientSurveyList(1);
                }
              }}
              className={classes.searchInput}
              width="17rem"
              height="2.5rem"
              placeholder="Ref. No / Keywords"
              endAdornment={
                <ButtonBase
                  onClick={() => {
                    setPageNum(1);
                    getPatientSurveyList(1);
                  }}
                >
                  <Box
                    width="1.875rem"
                    height="1.875rem"
                    borderRadius="5px"
                    bgcolor="#538fff"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <SearchIcon className={classes.searchIcon} />
                  </Box>
                </ButtonBase>
              }
            />
            <DateFilter
              title="Filter:"
              startDate={filter.startDate}
              stopDate={filter.endDate}
              setStartDate={(value) => debouncedSearch('startDate', value)}
              setEndDate={(value) => debouncedSearch('endDate', value)}
              startDatePickProps={{
                maxDate: filter.endDate,
                maxDateMessage: `Date should not be after maximal date`,
              }}
              stopDatePickProps={{
                minDate: filter.startDate,
                minDateMessage: `Date should not be before minimal date`,
              }}
            />
          </Box>
        </Box>
        <VirtualizedList data={list.data} Row={rowRender} onScroll={handleScroll} gap="1.25rem" overscanRowCount={3} />
      </Box>

      {/* popups */}
      {qrCodePopup && <SurveyGenQRCodePopup handleClose={() => setQrCodePopup(false)} />}
      {exportPopup && (
        <ExportSurveyPopup handleClose={() => setExportPopup(false)} filterWardLocation={filterWardLocation} />
      )}
      {senderContactPopup.open && (
        <SenderContactPopup
          handleClose={() => setSenderContactPopup({ open: false, responseDetailId: null })}
          responseDetailId={senderContactPopup?.responseDetailId}
        />
      )}
      {shareSurveyPopup.open && (
        <ShareSurveyPopup
          handleClose={() => setShareSurveyPopup({ open: false, responseId: null })}
          responseId={shareSurveyPopup?.responseId}
          handleConfirm={() => {
            setPageNum(1);
            getPatientSurveyList(1);
          }}
        />
      )}
      {editAppreciationPopup.open && (
        <EditAppreciationPopup
          handleClose={() => setEditAppreciationPopup({ open: false, data: null })}
          data={editAppreciationPopup?.data}
          handleConfirm={() => {
            setPageNum(1);
            getPatientSurveyList(1);
          }}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    top: '4.375rem',
    left: '6.25rem',
    right: '0',
    bottom: '0',
    display: 'flex',
    position: 'fixed',
    flexDirection: 'column',
    padding: '1.87rem 3.438rem 0',
    overflow: 'auto',
  },
  searchInput: {
    '&.MuiInputBase-root': {
      paddingRight: '0.37rem',
    },
  },
  searchIcon: {
    fontSize: '1.25rem',
    color: '#fff',
  },
}));

export default PatientSurvey;
